import { AlertTriangle, CheckCircle2, FileText, Loader2, RotateCw, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { mensajeDe } from '@/api/errors';
import {
  ACEPTA,
  nombreDeArchivo,
  subirArchivo,
  validarArchivo,
  type DestinoSubida,
  type TipoArchivo,
} from '@/lib/archivos';

export interface ArchivoEnCola {
  clave: string;
  file: File;
  nombre: string;
  estado: 'pendiente' | 'subiendo' | 'listo' | 'error';
  path?: string;
  url?: string | null;
  error?: string;
}

/** Archivos ya subidos, listos para mandarse en el alta (nombre editado + ruta del CDN). */
export interface ArchivoSubidoCola {
  nombre: string;
  path: string;
  url: string | null;
}

const PARALELO = 3;

/**
 * Cola de subida al CDN: el usuario elige o arrastra varios archivos, cada uno se sube en cuanto
 * entra (3 a la vez, con reintento) y su nombre se puede editar. `onCambio` recibe los que ya
 * están listos; el registro en la BD lo hace quien la usa (p. ej. `POST /flashcards/batch`).
 */
export function SubirVarios({
  destino,
  ownerId,
  tipos = ['image'],
  maxNombre = 120,
  onCambio,
  deshabilitado = false,
}: {
  destino: DestinoSubida;
  ownerId?: number;
  tipos?: TipoArchivo[];
  maxNombre?: number;
  onCambio: (listos: ArchivoSubidoCola[], pendientes: number) => void;
  deshabilitado?: boolean;
}) {
  const [cola, setCola] = useState<ArchivoEnCola[]>([]);
  const [arrastrando, setArrastrando] = useState(false);
  const enCurso = useRef(new Set<string>());
  const avisar = useRef(onCambio);
  useEffect(() => {
    avisar.current = onCambio;
  }, [onCambio]);

  const poner = (clave: string, cambio: Partial<ArchivoEnCola>) =>
    setCola((c) => c.map((a) => (a.clave === clave ? { ...a, ...cambio } : a)));

  // Arranca subidas pendientes hasta llenar los 3 lugares.
  useEffect(() => {
    const libres = PARALELO - enCurso.current.size;
    cola
      .filter((a) => a.estado === 'pendiente' && !enCurso.current.has(a.clave))
      .slice(0, Math.max(0, libres))
      .forEach((a) => {
        enCurso.current.add(a.clave);
        poner(a.clave, { estado: 'subiendo', error: undefined });
        subirArchivo(a.file, destino, ownerId)
          .then((r) => poner(a.clave, { estado: 'listo', path: r.path, url: r.url }))
          .catch((e: unknown) => poner(a.clave, { estado: 'error', error: mensajeDe(e) }))
          .finally(() => {
            enCurso.current.delete(a.clave);
            setCola((c) => [...c]);
          });
      });
    const listos = cola
      .filter((a) => a.estado === 'listo' && a.path)
      .map((a) => ({ nombre: a.nombre.trim(), path: a.path as string, url: a.url ?? null }));
    avisar.current(listos, cola.filter((a) => a.estado !== 'listo').length);
  }, [cola, destino, ownerId]);

  const agregar = (files: FileList | File[]) => {
    const nuevos = [...files].map((file): ArchivoEnCola => {
      const error = validarArchivo(file, tipos);
      return {
        clave: `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`,
        file,
        nombre: nombreDeArchivo(file).slice(0, maxNombre),
        estado: error ? 'error' : 'pendiente',
        error: error ?? undefined,
      };
    });
    setCola((c) => [...c, ...nuevos]);
  };

  const listos = cola.filter((a) => a.estado === 'listo').length;
  const accept = tipos.map((t) => ACEPTA[t]).join(',');
  return (
    <div className="space-y-3">
      {!deshabilitado && (
        <label
          className={`flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed p-6 text-center text-sm text-texto-2 transition ${
            arrastrando
              ? 'border-[var(--color-azul-600)] bg-[var(--color-azul-600)]/5'
              : 'border-[var(--separador)]'
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setArrastrando(true);
          }}
          onDragLeave={() => setArrastrando(false)}
          onDrop={(e) => {
            e.preventDefault();
            setArrastrando(false);
            agregar(e.dataTransfer.files);
          }}
        >
          <Upload size={22} aria-hidden />
          <span>
            <strong>Elige archivos</strong> o arrástralos aquí
          </span>
          <span className="text-xs text-texto-3">
            {tipos.includes('pdf') && tipos.includes('image')
              ? 'Imágenes (máx. 5 MB) o PDF (máx. 30 MB)'
              : tipos.includes('pdf')
                ? 'PDF (máx. 30 MB)'
                : 'JPG, PNG, WebP o GIF (máx. 5 MB)'}
          </span>
          <input
            type="file"
            multiple
            className="sr-only"
            accept={accept}
            onChange={(e) => {
              if (e.target.files) agregar(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      )}
      {cola.length > 0 && (
        <>
          <p className="text-xs text-texto-3">
            {listos} de {cola.length} subido(s). Puedes cambiar el nombre de cada uno.
          </p>
          <ul className="max-h-80 space-y-2 overflow-y-auto pr-1">
            {cola.map((a) => (
              <li key={a.clave} className="flex items-center gap-3 rounded-lg p-1">
                {a.url && a.file.type.startsWith('image/') ? (
                  <img src={a.url} alt="" className="h-12 w-16 shrink-0 rounded object-cover" />
                ) : (
                  <span className="grid h-12 w-16 shrink-0 place-items-center rounded border border-[var(--separador)] text-texto-3">
                    <FileText size={18} aria-hidden />
                  </span>
                )}
                <input
                  className="input-vidrio min-w-0 flex-1"
                  aria-label={`Nombre de ${a.file.name}`}
                  value={a.nombre}
                  maxLength={maxNombre}
                  disabled={deshabilitado}
                  onChange={(e) => poner(a.clave, { nombre: e.target.value })}
                />
                <span className="flex w-24 shrink-0 items-center gap-1 text-xs" title={a.error}>
                  {a.estado === 'subiendo' && (
                    <>
                      <Loader2 size={14} className="animate-spin" aria-hidden /> Subiendo
                    </>
                  )}
                  {a.estado === 'pendiente' && 'En espera'}
                  {a.estado === 'listo' && (
                    <>
                      <CheckCircle2 size={14} className="text-verde-texto" aria-hidden /> Listo
                    </>
                  )}
                  {a.estado === 'error' && (
                    <span className="flex items-center gap-1 font-semibold text-rojo-texto">
                      <AlertTriangle size={14} aria-hidden /> Error
                    </span>
                  )}
                </span>
                {a.estado === 'error' && !validarArchivo(a.file, tipos) && (
                  <button
                    type="button"
                    className="btn-cs btn-vidrio px-2"
                    aria-label="Reintentar"
                    onClick={() => poner(a.clave, { estado: 'pendiente' })}
                  >
                    <RotateCw size={14} aria-hidden />
                  </button>
                )}
                <button
                  type="button"
                  className="btn-cs btn-vidrio px-2"
                  aria-label={`Quitar ${a.file.name}`}
                  disabled={a.estado === 'subiendo'}
                  onClick={() => setCola((c) => c.filter((x) => x.clave !== a.clave))}
                >
                  <X size={14} aria-hidden />
                </button>
              </li>
            ))}
          </ul>
          {cola.some((a) => a.estado === 'error') && (
            <p className="text-xs text-rojo-texto">
              {cola
                .filter((a) => a.estado === 'error')
                .map((a) => `${a.file.name}: ${a.error ?? 'error'}`)
                .join(' · ')}
            </p>
          )}
        </>
      )}
    </div>
  );
}
